import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './ReportDashboard.css';
import SvgIcon from '../../components/SvgIcon';

// ── Filter Options & Constants ──
const LEVEL_OPTIONS = ['All', 'Awareness', 'Entry', 'Intermediate', 'Advanced'];
const STATUS_OPTIONS = ['All', 'Conducted', 'Upcoming', 'Postponed', 'Cancelled'];
const STANDARD_LEVELS = ['Awareness', 'Entry', 'Intermediate', 'Advanced'];

// Helper to format Date object as YYYY-MM-DD in local time
export const formatLocalDateStr = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to get today's date in YYYY-MM-DD
const getTodayDateStr = () => formatLocalDateStr(new Date());

// Helper to calculate weekly date range (Thursday to next Wednesday)
export const getWeeklyDateRange = (date = new Date()) => {
  const dayOfWeek = date.getDay(); // 0 = Sun, 1 = Mon, ..., 4 = Thu, 5 = Fri, 6 = Sat
  const diffToThursday = (dayOfWeek >= 4) ? (dayOfWeek - 4) : (dayOfWeek + 3);

  const startThursday = new Date(date);
  startThursday.setDate(date.getDate() - diffToThursday);

  const endWednesday = new Date(startThursday);
  endWednesday.setDate(startThursday.getDate() + 6);

  return {
    startDate: formatLocalDateStr(startThursday),
    endDate: formatLocalDateStr(endWednesday)
  };
};
// Helper to format Date + Time as YYYY-MM-DD hh:mm:ss AM/PM without rogue commas
export const formatReportDateTime = (d = new Date()) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  return `${year}-${month}-${day} ${timeStr}`;
};

// Helper to determine report title prefix (clean empty string)
export const getReportTitlePrefix = () => '';

// Helper to determine training type (In-House, Certifications, Others)
export const getCourseTrainingType = (cls) => {
  if (!cls) return 'Others';
  if (cls.trainingType) return cls.trainingType;

  const body = (cls.certificationBody || '').trim().toLowerCase();
  const title = (cls.title || '').trim().toLowerCase();
  const cType = (cls.type || '').trim().toLowerCase();

  if (cType.includes('in-house') || cType.includes('inhouse') || title.includes('in-house')) {
    return 'In-House';
  }
  const certBodies = ['comptia', 'isc2', 'pecb', 'ec-council', 'ecc'];
  if (certBodies.some(cb => body.includes(cb))) {
    return 'Certifications';
  }
  const inHouseBodies = ['blackberry', 'ccoe', 'rogers'];
  if (inHouseBodies.some(ih => body.includes(ih))) {
    return 'In-House';
  }
  return 'Others';
};

// Helper to determine delivery mode (Onsite, Offsite, Online)
export const getCourseDeliveryMode = (cls) => {
  if (!cls) return 'Onsite';
  const mode = (cls.deliveryMode || '').trim().toLowerCase();
  const venue = (cls.venue || '').trim().toLowerCase();
  const title = (cls.title || '').trim().toLowerCase();
  const type = (cls.type || '').trim().toLowerCase();

  if (mode === 'online' || venue.includes('online') || title.includes('online') || type === 'online') {
    return 'Online';
  }
  if (mode === 'offsite' || venue.includes('offsite') || title.includes('offsite')) {
    return 'Offsite';
  }
  return 'Onsite';
};

/**
 * Shared summary computation function across dashboard and exports
 * Rule 6.2: only sessions with Status = Conducted AND StartDate <= today count toward
 * classes conducted, registered pax, attended pax, sector totals, gender totals,
 * level counts, training hours and turn-up rate.
 */
export const computeSummary = (classes = [], todayStr = getTodayDateStr()) => {
  const totalClasses = classes.length;

  // Filter conducted past/today sessions
  const validConductedClasses = classes.filter(c => {
    const isConducted = c.status === 'Conducted';
    const isPastOrToday = c.startDate && c.startDate <= todayStr;
    return isConducted && isPastOrToday;
  });

  const totalClassesConducted = validConductedClasses.length;
  const totalCancelled = classes.filter(c => c.status === 'Cancelled').length;
  const totalPostponed = classes.filter(c => c.status === 'Postponed').length;
  const totalUpcoming = classes.filter(c => c.status === 'Conducted' && (!c.startDate || c.startDate > todayStr)).length;

  const activeClasses = classes.filter(c => c.status !== 'Cancelled' && c.status !== 'Postponed');
  const totalRegistrations = activeClasses.reduce((acc, c) => acc + (c.registeredPax || 0), 0);
  const totalAttendance = validConductedClasses.reduce((acc, c) => acc + (c.attendedPax || 0), 0);
  const totalHours = validConductedClasses.reduce((acc, c) => acc + (c.durationHours || 0), 0);

  // Turn-up rate is null ("N/A") only when Registered = 0
  const turnoutRate = totalRegistrations > 0
    ? ((totalAttendance / totalRegistrations) * 100).toFixed(1)
    : null;

  // Sector breakdown
  const sector = {
    government: { registered: 0, attended: 0, turnout: null },
    private: { registered: 0, attended: 0, turnout: null },
    student: { registered: 0, attended: 0, turnout: null }
  };

  activeClasses.forEach(c => {
    const reg = c.sectorBreakdown?.registered || {};
    sector.government.registered += reg.government || 0;
    sector.private.registered += reg.private || 0;
    sector.student.registered += reg.student || 0;
  });

  validConductedClasses.forEach(c => {
    const att = c.sectorBreakdown?.attended || {};
    sector.government.attended += att.government || 0;
    sector.private.attended += att.private || 0;
    sector.student.attended += att.student || 0;
  });

  ['government', 'private', 'student'].forEach(key => {
    const r = sector[key].registered;
    const a = sector[key].attended;
    sector[key].turnout = r > 0 ? ((a / r) * 100).toFixed(1) : null;
  });

  // Level summary
  const level = {
    Awareness: { count: 0, attended: 0 },
    Entry: { count: 0, attended: 0 },
    Intermediate: { count: 0, attended: 0 },
    Advanced: { count: 0, attended: 0 }
  };

  validConductedClasses.forEach(c => {
    const lvl = STANDARD_LEVELS.includes(c.level) ? c.level : 'Entry';
    if (level[lvl]) {
      level[lvl].count += 1;
      level[lvl].attended += (c.attendedPax || 0);
    }
  });

  // Training type summary (Requirement 1.a)
  // At this stage, the numbers for advanced courses are divided into In-House and Certification courses
  const trainingType = {
    inHouse: { count: 0, attended: 0, registered: 0 },
    certifications: { count: 0, attended: 0, registered: 0 },
    others: { count: 0, attended: 0, registered: 0 },
    total: { count: 0, attended: 0, registered: 0 }
  };

  validConductedClasses.forEach(c => {
    if (c.level === 'Advanced') {
      const t = getCourseTrainingType(c);
      if (t === 'In-House') {
        trainingType.inHouse.count += 1;
        trainingType.inHouse.attended += (c.attendedPax || 0);
      } else if (t === 'Certifications') {
        trainingType.certifications.count += 1;
        trainingType.certifications.attended += (c.attendedPax || 0);
      } else {
        trainingType.others.count += 1;
        trainingType.others.attended += (c.attendedPax || 0);
      }
      trainingType.total.count += 1;
      trainingType.total.attended += (c.attendedPax || 0);
    }
  });

  activeClasses.forEach(c => {
    if (c.level === 'Advanced') {
      const t = getCourseTrainingType(c);
      if (t === 'In-House') trainingType.inHouse.registered += (c.registeredPax || 0);
      else if (t === 'Certifications') trainingType.certifications.registered += (c.registeredPax || 0);
      else trainingType.others.registered += (c.registeredPax || 0);
      trainingType.total.registered += (c.registeredPax || 0);
    }
  });

  // Mode of course delivery (Requirement 1.b)
  // Total number of courses & total no of pax delivered through Onsite, Offsite & Online
  const deliveryMode = {
    onsite: { count: 0, attended: 0, registered: 0, turnout: null },
    offsite: { count: 0, attended: 0, registered: 0, turnout: null },
    online: { count: 0, attended: 0, registered: 0, turnout: null },
    total: { count: 0, attended: 0, registered: 0, turnout: null }
  };

  validConductedClasses.forEach(c => {
    const m = getCourseDeliveryMode(c).toLowerCase();
    if (deliveryMode[m]) {
      deliveryMode[m].count += 1;
      deliveryMode[m].attended += (c.attendedPax || 0);
    }
  });

  activeClasses.forEach(c => {
    const m = getCourseDeliveryMode(c).toLowerCase();
    if (deliveryMode[m]) {
      deliveryMode[m].registered += (c.registeredPax || 0);
    }
  });

  ['onsite', 'offsite', 'online'].forEach(key => {
    const r = deliveryMode[key].registered;
    const a = deliveryMode[key].attended;
    deliveryMode[key].turnout = r > 0 ? ((a / r) * 100).toFixed(1) : null;
  });

  deliveryMode.total.count = totalClassesConducted;
  deliveryMode.total.attended = totalAttendance;
  deliveryMode.total.registered = totalRegistrations;
  deliveryMode.total.turnout = turnoutRate;

  // Gender demographics (Attended & Registered)
  const gender = {
    male: 0,
    female: 0,
    preferNotToSay: 0,
    attended: { male: 0, female: 0, preferNotToSay: 0 },
    registered: { male: 0, female: 0, preferNotToSay: 0 }
  };

  validConductedClasses.forEach(c => {
    const attM = c.genderBreakdown?.attended?.male ?? c.genderBreakdown?.male ?? 0;
    const attF = c.genderBreakdown?.attended?.female ?? c.genderBreakdown?.female ?? 0;
    const attO = c.genderBreakdown?.attended?.preferNotToSay ?? c.genderBreakdown?.preferNotToSay ?? 0;

    gender.attended.male += attM;
    gender.attended.female += attF;
    gender.attended.preferNotToSay += attO;
    gender.male += attM;
    gender.female += attF;
    gender.preferNotToSay += attO;
  });

  activeClasses.forEach(c => {
    let regM = c.genderBreakdown?.registered?.male ?? c.registeredGenderBreakdown?.male;
    let regF = c.genderBreakdown?.registered?.female ?? c.registeredGenderBreakdown?.female;
    let regO = c.genderBreakdown?.registered?.preferNotToSay ?? c.registeredGenderBreakdown?.preferNotToSay;

    if (regM === undefined && c.learners) {
      regM = c.learners.filter(l => (l.gender || '').toLowerCase() === 'male').length;
      regF = c.learners.filter(l => (l.gender || '').toLowerCase() === 'female').length;
      regO = c.learners.length - (regM + regF);
    }

    gender.registered.male += (regM || 0);
    gender.registered.female += (regF || 0);
    gender.registered.preferNotToSay += (regO || 0);
  });

  return {
    totalClasses,
    totalClassesConducted,
    totalCancelled,
    totalPostponed,
    totalUpcoming,
    totalRegistrations,
    totalAttendance,
    totalHours,
    turnoutRate,
    sector,
    level,
    gender,
    trainingType,
    deliveryMode,
    validConductedClasses
  };
};

// ── Export Sanitizer ──
const sanitize = (str) => {
  if (str === null || str === undefined) return '""';
  const cleanStr = String(str).replace(/"/g, '""');
  return `"${cleanStr}"`;
};

// ── Export Class Breakdown Report CSV ──
export const exportClassBreakdownCSV = (classes, startDate, endDate, todayStr = getTodayDateStr()) => {
  let csv = 'BLACKBERRY CCoE - REPORT 2: CLASS BREAKDOWN REPORT\r\n';
  csv += `Date Range: ${startDate || 'All'} to ${endDate || 'All'}\r\n`;
  csv += `Generated On: ${formatReportDateTime()}\r\n`;
  csv += `Total Sessions Listed: ${classes.length}\r\n\r\n`;

  // Requirement 2.a: Registered Male, Registered Female & Registered Prefer Not To Say before attended genders
  csv += 'No,Class ID,Name of Class,Trainer,Delivery Mode,Venue,Certification Body,Level,Status,Start Date,End Date,Time,Duration (Hrs),Registered Pax,Attended Pax,Turn-up Rate (%),Gov Reg,Gov Att,Priv Reg,Priv Att,Stu Reg,Stu Att,Registered Male,Registered Female,Registered Prefer Not To Say,Attended Male,Attended Female,Attended Prefer Not To Say,Remarks\r\n';

  classes.forEach((cls, idx) => {
    let displayStatus = cls.status;
    if (cls.status === 'Conducted' && cls.startDate > todayStr) {
      displayStatus = 'Upcoming';
    } else if (cls.status === 'Postponed') {
      displayStatus = 'Postponed (legacy)';
    }

    const turnout = cls.registeredPax > 0
      ? `${((cls.attendedPax / cls.registeredPax) * 100).toFixed(1)}%`
      : 'N/A';

    let regMale = cls.genderBreakdown?.registered?.male ?? cls.registeredGenderBreakdown?.male;
    let regFemale = cls.genderBreakdown?.registered?.female ?? cls.registeredGenderBreakdown?.female;
    let regOther = cls.genderBreakdown?.registered?.preferNotToSay ?? cls.registeredGenderBreakdown?.preferNotToSay;

    if (regMale === undefined && cls.learners) {
      regMale = cls.learners.filter(l => (l.gender || '').toLowerCase() === 'male').length;
      regFemale = cls.learners.filter(l => (l.gender || '').toLowerCase() === 'female').length;
      regOther = cls.learners.length - (regMale + regFemale);
    }

    const attMale = cls.genderBreakdown?.attended?.male ?? cls.genderBreakdown?.male ?? 0;
    const attFemale = cls.genderBreakdown?.attended?.female ?? cls.genderBreakdown?.female ?? 0;
    const attOther = cls.genderBreakdown?.attended?.preferNotToSay ?? cls.genderBreakdown?.preferNotToSay ?? 0;

    const row = [
      idx + 1,
      sanitize(cls.id),
      sanitize(cls.title),
      sanitize(cls.trainer),
      sanitize(getCourseDeliveryMode(cls)),
      sanitize(cls.venue),
      sanitize(cls.certificationBody),
      sanitize(cls.level),
      sanitize(displayStatus),
      sanitize(cls.startDate),
      sanitize(cls.endDate),
      sanitize(`${cls.startTime} - ${cls.endTime}`),
      cls.durationHours,
      cls.registeredPax,
      cls.attendedPax,
      sanitize(turnout),
      cls.sectorBreakdown?.registered?.government || 0,
      cls.sectorBreakdown?.attended?.government || 0,
      cls.sectorBreakdown?.registered?.private || 0,
      cls.sectorBreakdown?.attended?.private || 0,
      cls.sectorBreakdown?.registered?.student || 0,
      cls.sectorBreakdown?.attended?.student || 0,
      regMale || 0,
      regFemale || 0,
      regOther || 0,
      attMale,
      attFemale,
      attOther,
      sanitize(cls.remarks || '')
    ];
    csv += row.join(',') + '\r\n';
  });

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `class_breakdown_report_${startDate || 'all'}_${endDate || 'all'}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ── Export Summary Report CSV ──
export const exportSummaryReportCSV = (classes, startDate, endDate, todayStr = getTodayDateStr()) => {
  const summary = computeSummary(classes, todayStr);

  let csv = 'BLACKBERRY CCoE - REPORT 1: SUMMARY REPORT\r\n';
  csv += `Date Range: ${startDate || 'All'} to ${endDate || 'All'}\r\n`;
  csv += `Generated On: ${formatReportDateTime()}\r\n\r\n`;

  csv += '==================================================\r\n';
  csv += 'SECTION 1: EXECUTIVE KPI SUMMARY\r\n';
  csv += '==================================================\r\n';
  csv += 'Metric,Value\r\n';
  csv += `Total Classes Conducted,${summary.totalClassesConducted}\r\n`;
  csv += `Total Classes Scheduled,${summary.totalClasses}\r\n`;
  csv += `Cancelled / Postponed Classes,${summary.totalCancelled + summary.totalPostponed}\r\n`;
  csv += `Upcoming Classes,${summary.totalUpcoming}\r\n`;
  csv += `Total Registrations (Pax),${summary.totalRegistrations}\r\n`;
  csv += `Total Attendance (Pax),${summary.totalAttendance}\r\n`;
  csv += `Turn-up Rate (%),${summary.turnoutRate !== null ? summary.turnoutRate + '%' : 'N/A'}\r\n`;
  csv += `Total Training Hours,${summary.totalHours} hrs\r\n\r\n`;

  csv += '==================================================\r\n';
  csv += 'SECTION 2: SECTOR DISTRIBUTION SUMMARY\r\n';
  csv += '==================================================\r\n';
  csv += 'Sector,Registered (Pax),Attended (Pax),Turn-up (%)\r\n';
  csv += `Government,${summary.sector.government.registered},${summary.sector.government.attended},${summary.sector.government.turnout !== null ? summary.sector.government.turnout + '%' : 'N/A'}\r\n`;
  csv += `Private,${summary.sector.private.registered},${summary.sector.private.attended},${summary.sector.private.turnout !== null ? summary.sector.private.turnout + '%' : 'N/A'}\r\n`;
  csv += `Student,${summary.sector.student.registered},${summary.sector.student.attended},${summary.sector.student.turnout !== null ? summary.sector.student.turnout + '%' : 'N/A'}\r\n`;
  csv += `Total,${summary.totalRegistrations},${summary.totalAttendance},${summary.turnoutRate !== null ? summary.turnoutRate + '%' : 'N/A'}\r\n\r\n`;

  csv += '==================================================\r\n';
  csv += 'SECTION 3: CLASS BY LEVEL\r\n';
  csv += '==================================================\r\n';
  csv += 'Level,Class Count\r\n';
  STANDARD_LEVELS.forEach(lvl => {
    csv += `${lvl},${summary.level[lvl].count}\r\n`;
  });
  csv += `Total,${summary.totalClassesConducted}\r\n\r\n`;

  csv += '==================================================\r\n';
  csv += 'SECTION 4: PARTICIPANTS BY CLASS LEVEL\r\n';
  csv += '==================================================\r\n';
  csv += 'Level,Attended (Pax)\r\n';
  STANDARD_LEVELS.forEach(lvl => {
    csv += `${lvl},${summary.level[lvl].attended}\r\n`;
  });
  csv += `Total,${summary.totalAttendance}\r\n\r\n`;

  // Requirement 1.a: Type of Courses for In-House, Certifications & Others
  csv += '==================================================\r\n';
  csv += 'SECTION 5: TYPE OF COURSES (ADVANCED)\r\n';
  csv += '==================================================\r\n';
  csv += 'Trainings,Class Count\r\n';
  csv += `In-House,${summary.trainingType.inHouse.count}\r\n`;
  csv += `Certifications,${summary.trainingType.certifications.count}\r\n`;
  csv += `Others,${summary.trainingType.others.count}\r\n`;
  csv += `Total,${summary.trainingType.total.count}\r\n\r\n`;

  // Requirement 1.b: Mode of Course Delivery (Onsite, Offsite & Online)
  csv += '==================================================\r\n';
  csv += 'SECTION 6: MODE OF COURSE DELIVERY\r\n';
  csv += '==================================================\r\n';
  csv += 'Delivery Mode,Total Courses,Attended (Pax),Registered (Pax),Turn-up (%)\r\n';
  csv += `Onsite,${summary.deliveryMode.onsite.count},${summary.deliveryMode.onsite.attended},${summary.deliveryMode.onsite.registered},${summary.deliveryMode.onsite.turnout !== null ? summary.deliveryMode.onsite.turnout + '%' : 'N/A'}\r\n`;
  csv += `Offsite,${summary.deliveryMode.offsite.count},${summary.deliveryMode.offsite.attended},${summary.deliveryMode.offsite.registered},${summary.deliveryMode.offsite.turnout !== null ? summary.deliveryMode.offsite.turnout + '%' : 'N/A'}\r\n`;
  csv += `Online,${summary.deliveryMode.online.count},${summary.deliveryMode.online.attended},${summary.deliveryMode.online.registered},${summary.deliveryMode.online.turnout !== null ? summary.deliveryMode.online.turnout + '%' : 'N/A'}\r\n`;
  csv += `Total,${summary.deliveryMode.total.count},${summary.deliveryMode.total.attended},${summary.deliveryMode.total.registered},${summary.turnoutRate !== null ? summary.turnoutRate + '%' : 'N/A'}\r\n\r\n`;

  csv += '==================================================\r\n';
  csv += 'SECTION 7: GENDER DEMOGRAPHICS (ATTENDED)\r\n';
  csv += '==================================================\r\n';
  csv += 'Gender,Attended (Pax),Percentage (%)\r\n';
  const malePct = summary.totalAttendance > 0 ? ((summary.gender.male / summary.totalAttendance) * 100).toFixed(1) : '0.0';
  const femalePct = summary.totalAttendance > 0 ? ((summary.gender.female / summary.totalAttendance) * 100).toFixed(1) : '0.0';
  const otherPct = summary.totalAttendance > 0 ? ((summary.gender.preferNotToSay / summary.totalAttendance) * 100).toFixed(1) : '0.0';
  csv += `Male,${summary.gender.male},${malePct}%\r\n`;
  csv += `Female,${summary.gender.female},${femalePct}%\r\n`;
  csv += `Prefer Not To Say,${summary.gender.preferNotToSay},${otherPct}%\r\n`;
  csv += `Total,${summary.totalAttendance},100.0%\r\n\r\n`;

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `summary_report_${startDate || 'all'}_${endDate || 'all'}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ── Export Learner Registration Log CSV (Report 3) ──
export const exportLearnerRegistrationLogCSV = (classes, startDate, endDate, todayStr = getTodayDateStr()) => {
  let csv = 'BLACKBERRY CCoE - REPORT 3: LEARNER REGISTRATION LOG\r\n';
  csv += `Date Range: ${startDate || 'All'} to ${endDate || 'All'}\r\n`;
  csv += `Generated On: ${formatReportDateTime()}\r\n\r\n`;

  csv += '==================================================\r\n';
  csv += 'SECTION 1: LEARNER REGISTRATION & PARTICIPATION LOG\r\n';
  csv += '==================================================\r\n';
  csv += 'No,Username,Gender,Sector,Course Name,Start Date,End Date,Registration Status,Participation Status\r\n';

  let evidenceIdx = 1;
  classes.forEach(cls => {
    const isConducted = cls.status === 'Conducted' && cls.startDate <= todayStr;
    const learnerList = cls.learners || [];
    learnerList.forEach(lrn => {
      const regStatus = (lrn.registrationStatus === 'Approved') ? 'Approved' : 'Pending';
      const partStatus = (isConducted && lrn.participationStatus === 'Attended') ? 'Attended' : 'Registered Only';
      const row = [
        evidenceIdx++,
        sanitize(lrn.username),
        sanitize(lrn.gender),
        sanitize(lrn.sector),
        sanitize(cls.title),
        sanitize(cls.startDate),
        sanitize(cls.endDate),
        sanitize(regStatus),
        sanitize(partStatus)
      ];
      csv += row.join(',') + '\r\n';
    });
  });
  if (evidenceIdx === 1) {
    csv += 'No individual participant registrations recorded for the selected date range.\r\n';
  }
  csv += '\r\n';

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `learner_registration_log_${startDate || 'all'}_${endDate || 'all'}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ── SUB-COMPONENT 1: Filter Panel ──
const ReportFilters = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onOpenExportModal
}) => {
  return (
    <div className="report-filter-panel no-print">
      <div className="report-filter-left">
        <div className="filter-date-inputs">
          <div className="date-input-wrapper">
            <label htmlFor="report-start-date">From</label>
            <input
              id="report-start-date"
              type="date"
              className="report-date-field"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <span className="date-separator">→</span>
          <div className="date-input-wrapper">
            <label htmlFor="report-end-date">To</label>
            <input
              id="report-end-date"
              type="date"
              className="report-date-field"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="report-filter-right">
        <button
          type="button"
          className="export-btn export-unified"
          onClick={onOpenExportModal}
          title="Export Summary Report, Class Breakdown Report, or Learner Registration Log as Excel (.csv)"
        >
          <SvgIcon name="download" size={16} />
          <span>Export Reports</span>
        </button>
      </div>
    </div>
  );
};

// ── SUB-COMPONENT 2: KPI Summary Cards ──
const KPISummary = ({ kpis }) => {
  return (
    <div className="report-kpi-grid">
      <div className="report-kpi-card">
        <div className="kpi-card-header">
          <span className="kpi-card-title">Total Classes Conducted</span>
          <div className="kpi-icon-badge kpi-indigo">
            <SvgIcon name="book" size={20} />
          </div>
        </div>
        <div className="kpi-card-body">
          <span className="kpi-card-value">{kpis.totalClassesConducted}</span>
          <span className="kpi-card-subtext">
            <span>
              <strong>{kpis.totalClasses}</strong> total scheduled ({kpis.totalCancelled + kpis.totalPostponed} cancelled/postponed, {kpis.totalUpcoming} upcoming)
            </span>
          </span>
        </div>
        <div className="kpi-card-indicator indicator-indigo" />
      </div>

      <div className="report-kpi-card">
        <div className="kpi-card-header">
          <span className="kpi-card-title">Total Registrations</span>
          <div className="kpi-icon-badge kpi-blue">
            <SvgIcon name="clipboard" size={20} />
          </div>
        </div>
        <div className="kpi-card-body">
          <span className="kpi-card-value">{kpis.totalRegistrations.toLocaleString()}</span>
          <span className="kpi-card-subtext">Total learner pax registered</span>
        </div>
        <div className="kpi-card-indicator indicator-blue" />
      </div>

      <div className="report-kpi-card">
        <div className="kpi-card-header">
          <span className="kpi-card-title">Total Attendance</span>
          <div className="kpi-icon-badge kpi-emerald">
            <SvgIcon name="users" size={20} />
          </div>
        </div>
        <div className="kpi-card-body">
          <span className="kpi-card-value">{kpis.totalAttendance.toLocaleString()}</span>
          <span className="kpi-card-subtext">Verified present in sessions</span>
        </div>
        <div className="kpi-card-indicator indicator-emerald" />
      </div>

      <div className="report-kpi-card">
        <div className="kpi-card-header">
          <span className="kpi-card-title">Turn-up Rate</span>
          <div className="kpi-icon-badge kpi-amber">
            <SvgIcon name="award" size={20} />
          </div>
        </div>
        <div className="kpi-card-body">
          <span className="kpi-card-value">
            {kpis.turnoutRate !== null ? `${kpis.turnoutRate}%` : 'N/A'}
          </span>
          <span className="kpi-card-subtext">
            (Total Attended ÷ Total Registered)
          </span>
        </div>
        <div className="kpi-card-indicator indicator-amber" />
      </div>

      <div className="report-kpi-card">
        <div className="kpi-card-header">
          <span className="kpi-card-title">Total Training Hours</span>
          <div className="kpi-icon-badge kpi-purple">
            <SvgIcon name="clock" size={20} />
          </div>
        </div>
        <div className="kpi-card-body">
          <span className="kpi-card-value">{kpis.totalHours} <span className="kpi-unit">hrs</span></span>
          <span className="kpi-card-subtext">
            Avg. {(kpis.totalClassesConducted > 0 ? (kpis.totalHours / kpis.totalClassesConducted).toFixed(1) : 0)} hrs/class
          </span>
        </div>
        <div className="kpi-card-indicator indicator-purple" />
      </div>
    </div>
  );
};

// ── SUB-COMPONENT 3: Analytics & Visualizations Grid ──
const ReportCharts = ({
  sectorData,
  levelData,
  genderData,
  trendData,
  totalConducted,
  totalAttendance,
  trainingTypeData,
  deliveryModeData
}) => {
  const [activeTooltip, setActiveTooltip] = useState(null);

  const trainingType = trainingTypeData || {
    inHouse: { count: 0, attended: 0, registered: 0 },
    certifications: { count: 0, attended: 0, registered: 0 },
    others: { count: 0, attended: 0, registered: 0 },
    total: { count: 0, attended: 0, registered: 0 }
  };

  const deliveryMode = deliveryModeData || {
    onsite: { count: 0, attended: 0, registered: 0, turnout: null },
    offsite: { count: 0, attended: 0, registered: 0, turnout: null },
    online: { count: 0, attended: 0, registered: 0, turnout: null },
    total: { count: 0, attended: 0, registered: 0, turnout: null }
  };

  const sectors = [
    { key: 'government', label: 'Government', colorReg: '#93c5fd', colorAtt: '#2563eb' },
    { key: 'private', label: 'Private', colorReg: '#a7f3d0', colorAtt: '#059669' },
    { key: 'student', label: 'Student', colorReg: '#fde68a', colorAtt: '#d97706' }
  ];

  const maxSectorVal = Math.max(
    ...sectors.map(s => Math.max(sectorData[s.key]?.registered || 0, sectorData[s.key]?.attended || 0)),
    10
  );

  const levels = STANDARD_LEVELS;
  const maxLevelAttended = Math.max(...levels.map(l => levelData[l]?.attended || 0), 10);
  const maxLevelCount = Math.max(...levels.map(l => levelData[l]?.count || 0), 5);

  const totalGender = (genderData.male || 0) + (genderData.female || 0) + (genderData.preferNotToSay || 0);
  const malePct = totalGender > 0 ? ((genderData.male / totalGender) * 100) : 0;
  const femalePct = totalGender > 0 ? ((genderData.female / totalGender) * 100) : 0;
  const otherPct = totalGender > 0 ? ((genderData.preferNotToSay / totalGender) * 100) : 0;

  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const maleStroke = (malePct / 100) * circumference;
  const femaleStroke = (femalePct / 100) * circumference;
  const otherStroke = (otherPct / 100) * circumference;

  const maxTrendVal = Math.max(
    ...trendData.map(d => Math.max(d.registered, d.attended)),
    10
  );
  const svgWidth = 460;
  const svgHeight = 160;
  const paddingX = 40;
  const paddingY = 20;
  const chartWidth = svgWidth - paddingX * 2;
  const chartHeight = svgHeight - paddingY * 2;

  const getCoordinates = (val, idx, total) => {
    const x = paddingX + (idx / Math.max(total - 1, 1)) * chartWidth;
    const y = svgHeight - paddingY - (val / maxTrendVal) * chartHeight;
    return { x, y };
  };

  const regPoints = trendData.map((d, i) => getCoordinates(d.registered, i, trendData.length));
  const attPoints = trendData.map((d, i) => getCoordinates(d.attended, i, trendData.length));
  const regPolyline = regPoints.map(p => `${p.x},${p.y}`).join(' ');
  const attPolyline = attPoints.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <div className="report-charts-grid">
      {/* 1. Sector Distribution */}
      <div className="report-chart-card">
        <div className="chart-card-header">
          <div className="chart-title-box">
            <SvgIcon name="barChart" size={18} />
            <span className="chart-title">Sector Distribution</span>
          </div>
          <span className="chart-subtitle">Registered vs. Attended Pax</span>
        </div>

        <div className="chart-legend-box">
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#94a3b8' }} />
            <span>Registered</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#2563eb' }} />
            <span>Attended</span>
          </div>
        </div>

        <div className="sector-bar-chart-container">
          {sectors.map(sec => {
            const reg = sectorData[sec.key]?.registered || 0;
            const att = sectorData[sec.key]?.attended || 0;
            const turnout = reg > 0 ? `${((att / reg) * 100).toFixed(1)}%` : 'N/A';
            const regWidth = (reg / maxSectorVal) * 100;
            const attWidth = (att / maxSectorVal) * 100;

            return (
              <div key={sec.key} className="sector-bar-group">
                <div className="sector-label-row">
                  <span className="sector-name">{sec.label}</span>
                  <span className="sector-turnout-pill">{turnout} turn-up</span>
                </div>
                <div className="sector-bars-stack">
                  <div className="bar-track">
                    <div className="bar-fill reg-fill" style={{ width: `${regWidth}%` }} title={`Registered: ${reg}`}>
                      {reg > 0 && <span className="bar-value-label">{reg}</span>}
                    </div>
                  </div>
                  <div className="bar-track">
                    <div className="bar-fill att-fill" style={{ width: `${attWidth}%`, backgroundColor: sec.colorAtt }} title={`Attended: ${att}`}>
                      {att > 0 && <span className="bar-value-label">{att}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 2. Class & Attendance by Level */}
      <div className="report-chart-card">
        <div className="chart-card-header">
          <div className="chart-title-box">
            <SvgIcon name="award" size={18} />
            <span className="chart-title">Class & Attendance by Level</span>
          </div>
          <span className="chart-subtitle">Conducted Sessions & Attended Pax</span>
        </div>

        <div className="chart-legend-box">
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#6366f1' }} />
            <span>Attended participants</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#cbd5e1' }} />
            <span>Classes</span>
          </div>
        </div>

        <div className="level-column-chart">
          {levels.map(lvl => {
            const count = levelData[lvl]?.count || 0;
            const att = levelData[lvl]?.attended || 0;
            const heightAtt = maxLevelAttended > 0 ? (att / maxLevelAttended) * 100 : 0;
            const heightCount = maxLevelCount > 0 ? (count / maxLevelCount) * 100 : 0;

            return (
              <div key={lvl} className="level-column-item">
                <div className="column-bars-wrapper">
                  <div className="column-bar-fill level-att-bar" style={{ height: `${Math.max(heightAtt, 6)}%` }} title={`${lvl}: ${att} Attended`}>
                    <span className="column-bar-tip">{att}</span>
                  </div>
                  <div className="column-bar-fill level-count-bar" style={{ height: `${Math.max(heightCount, 6)}%` }} title={`${lvl}: ${count} Class(es)`}>
                    <span className="column-bar-tip-count">{count}</span>
                  </div>
                </div>
                <span className="level-col-label">{lvl}</span>
              </div>
            );
          })}
        </div>

        {/* Level Summary Subtables */}
        <div className="level-summary-tables-container">
          <div className="level-subtable-box">
            <span className="subtable-title">Class by Level</span>
            <table className="level-mini-table">
              <thead>
                <tr>
                  <th>Level</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {levels.map(lvl => (
                  <tr key={`tbl-cls-${lvl}`}>
                    <td>{lvl}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{levelData[lvl]?.count || 0}</td>
                  </tr>
                ))}
                <tr className="level-subtable-total">
                  <td><strong>Total</strong></td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{totalConducted}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="level-subtable-box">
            <span className="subtable-title">Participants by Class Level</span>
            <table className="level-mini-table">
              <thead>
                <tr>
                  <th>Level</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {levels.map(lvl => (
                  <tr key={`tbl-att-${lvl}`}>
                    <td>{lvl}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{levelData[lvl]?.attended || 0}</td>
                  </tr>
                ))}
                <tr className="level-subtable-total">
                  <td><strong>Total</strong></td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{totalAttendance}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 3. Type of Courses (In-House, Certifications & Others) */}
      <div className="report-chart-card">
        <div className="chart-card-header">
          <div className="chart-title-box">
            <SvgIcon name="book" size={18} />
            <span className="chart-title">Type of Courses (Advanced)</span>
          </div>
          <span className="chart-subtitle">In-House, Certifications & Others</span>
        </div>

        <div className="training-type-table-container">
          <table className="training-type-table">
            <thead>
              <tr className="training-table-super-header">
                <th colSpan="3" className="trainings-merged-header">
                  Advanced Training Conducted
                </th>
              </tr>
              <tr className="training-table-sub-header">
                <th style={{ textAlign: 'center', width: '33.33%' }}>In-House</th>
                <th style={{ textAlign: 'center', width: '33.33%' }}>Certifications</th>
                <th style={{ textAlign: 'center', width: '33.33%' }}>Others</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ textAlign: 'center', fontWeight: 800, fontSize: '1.4rem', color: '#0f172a', padding: '1.25rem 0.75rem' }}>
                  {trainingType.inHouse.count}
                </td>
                <td style={{ textAlign: 'center', fontWeight: 800, fontSize: '1.4rem', color: '#0f172a', padding: '1.25rem 0.75rem' }}>
                  {trainingType.certifications.count}
                </td>
                <td style={{ textAlign: 'center', fontWeight: 800, fontSize: '1.4rem', color: '#64748b', padding: '1.25rem 0.75rem' }}>
                  {trainingType.others.count}
                </td>
              </tr>
              <tr className="training-type-total-row">
                <td style={{ textAlign: 'center', fontWeight: 700, padding: '0.65rem 0.75rem' }} colSpan="3">
                  Total Advanced Training Conducted: {trainingType.total.count}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. Mode of Course Delivery */}
      <div className="report-chart-card">
        <div className="chart-card-header">
          <div className="chart-title-box">
            <SvgIcon name="calendar" size={18} />
            <span className="chart-title">Mode of Course Delivery</span>
          </div>
          <span className="chart-subtitle">Onsite, Offsite & Online Delivery Breakdown</span>
        </div>

        <div className="delivery-mode-table-container">
          <table className="delivery-mode-table">
            <thead>
              <tr>
                <th>Delivery Mode</th>
                <th style={{ textAlign: 'center' }}>Total Courses</th>
                <th style={{ textAlign: 'center' }}>Attended Pax</th>
                <th style={{ textAlign: 'center' }}>Registered Pax</th>
                <th style={{ textAlign: 'center' }}>Turn-up</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="mode-label-row">
                    <span className="mode-badge-indicator mode-onsite" />
                    <strong>Onsite</strong>
                  </div>
                </td>
                <td style={{ textAlign: 'center', fontWeight: 600 }}>{deliveryMode.onsite.count}</td>
                <td style={{ textAlign: 'center', fontWeight: 700, color: '#2563eb' }}>{deliveryMode.onsite.attended}</td>
                <td style={{ textAlign: 'center', color: '#475569' }}>{deliveryMode.onsite.registered}</td>
                <td style={{ textAlign: 'center' }}>
                  <span className="mode-turnout-tag">{deliveryMode.onsite.turnout !== null ? `${deliveryMode.onsite.turnout}%` : 'N/A'}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <div className="mode-label-row">
                    <span className="mode-badge-indicator mode-offsite" />
                    <strong>Offsite</strong>
                  </div>
                </td>
                <td style={{ textAlign: 'center', fontWeight: 600 }}>{deliveryMode.offsite.count}</td>
                <td style={{ textAlign: 'center', fontWeight: 700, color: '#059669' }}>{deliveryMode.offsite.attended}</td>
                <td style={{ textAlign: 'center', color: '#475569' }}>{deliveryMode.offsite.registered}</td>
                <td style={{ textAlign: 'center' }}>
                  <span className="mode-turnout-tag">{deliveryMode.offsite.turnout !== null ? `${deliveryMode.offsite.turnout}%` : 'N/A'}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <div className="mode-label-row">
                    <span className="mode-badge-indicator mode-online" />
                    <strong>Online</strong>
                  </div>
                </td>
                <td style={{ textAlign: 'center', fontWeight: 600 }}>{deliveryMode.online.count}</td>
                <td style={{ textAlign: 'center', fontWeight: 700, color: '#7c3aed' }}>{deliveryMode.online.attended}</td>
                <td style={{ textAlign: 'center', color: '#475569' }}>{deliveryMode.online.registered}</td>
                <td style={{ textAlign: 'center' }}>
                  <span className="mode-turnout-tag">{deliveryMode.online.turnout !== null ? `${deliveryMode.online.turnout}%` : 'N/A'}</span>
                </td>
              </tr>
              <tr className="delivery-mode-total-row">
                <td><strong>Total</strong></td>
                <td style={{ textAlign: 'center', fontWeight: 700 }}>{deliveryMode.total.count}</td>
                <td style={{ textAlign: 'center', fontWeight: 700, color: '#0f172a' }}>{deliveryMode.total.attended}</td>
                <td style={{ textAlign: 'center', fontWeight: 700 }}>{deliveryMode.total.registered}</td>
                <td style={{ textAlign: 'center', fontWeight: 700 }}>{deliveryMode.total.turnout !== null ? `${deliveryMode.total.turnout}%` : 'N/A'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 5. Gender Breakdown */}
      <div className="report-chart-card">
        <div className="chart-card-header">
          <div className="chart-title-box">
            <SvgIcon name="users" size={18} />
            <span className="chart-title">Gender Breakdown</span>
          </div>
          <span className="chart-subtitle">Attended Participants ({totalGender} Total)</span>
        </div>

        <div className="gender-donut-container">
          <div className="donut-svg-wrapper">
            <svg width="150" height="150" viewBox="0 0 150 150" className="donut-svg">
              <circle cx="75" cy="75" r={radius} fill="transparent" stroke="#f1f5f9" strokeWidth="18" />
              <circle cx="75" cy="75" r={radius} fill="transparent" stroke="#3b82f6" strokeWidth="18" strokeDasharray={`${maleStroke} ${circumference}`} strokeDashoffset="0" transform="rotate(-90 75 75)" />
              <circle cx="75" cy="75" r={radius} fill="transparent" stroke="#ec4899" strokeWidth="18" strokeDasharray={`${femaleStroke} ${circumference}`} strokeDashoffset={-maleStroke} transform="rotate(-90 75 75)" />
              <circle cx="75" cy="75" r={radius} fill="transparent" stroke="#94a3b8" strokeWidth="18" strokeDasharray={`${otherStroke} ${circumference}`} strokeDashoffset={-(maleStroke + femaleStroke)} transform="rotate(-90 75 75)" />
            </svg>
            <div className="donut-center-info">
              <span className="donut-center-count">{totalGender}</span>
              <span className="donut-center-text">Pax</span>
            </div>
          </div>

          <div className="gender-breakdown-legend">
            <div className="gender-row">
              <div className="gender-indicator-box">
                <span className="legend-dot" style={{ backgroundColor: '#3b82f6' }} />
                <span className="gender-name">Male</span>
              </div>
              <span className="gender-numbers">
                <strong>{genderData.male || 0}</strong> ({malePct.toFixed(1)}%)
              </span>
            </div>
            <div className="gender-row">
              <div className="gender-indicator-box">
                <span className="legend-dot" style={{ backgroundColor: '#ec4899' }} />
                <span className="gender-name">Female</span>
              </div>
              <span className="gender-numbers">
                <strong>{genderData.female || 0}</strong> ({femalePct.toFixed(1)}%)
              </span>
            </div>
            <div className="gender-row">
              <div className="gender-indicator-box">
                <span className="legend-dot" style={{ backgroundColor: '#94a3b8' }} />
                <span className="gender-name">Prefer Not To Say</span>
              </div>
              <span className="gender-numbers">
                <strong>{genderData.preferNotToSay || 0}</strong> ({otherPct.toFixed(1)}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Attendance Trend Over Time (Weekly or Monthly) */}
      <div className="report-chart-card">
        <div className="chart-card-header">
          <div className="chart-title-box">
            <SvgIcon name="calendar" size={18} />
            <span className="chart-title">Attendance Trend Over Time</span>
          </div>
          <span className="chart-subtitle">Registrations vs. Attendance Timeline</span>
        </div>

        <div className="chart-legend-box">
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#94a3b8' }} />
            <span>Registered</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#4f46e5' }} />
            <span>Attended</span>
          </div>
        </div>

        <div className="trend-svg-container">
          {trendData.length > 0 ? (
            <svg width="100%" height="160" viewBox={`0 0 ${svgWidth} ${svgHeight}`} preserveAspectRatio="none" className="trend-svg">
              <line x1={paddingX} y1={paddingY} x2={svgWidth - paddingX} y2={paddingY} stroke="#f1f5f9" strokeDasharray="3 3" />
              <line x1={paddingX} y1={svgHeight / 2} x2={svgWidth - paddingX} y2={svgHeight / 2} stroke="#f1f5f9" strokeDasharray="3 3" />
              <line x1={paddingX} y1={svgHeight - paddingY} x2={svgWidth - paddingX} y2={svgHeight - paddingY} stroke="#e2e8f0" />
              <polyline fill="none" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="4 4" points={regPolyline} />
              <polyline fill="none" stroke="#4f46e5" strokeWidth="3" points={attPolyline} />
              {regPoints.map((p, idx) => (
                <circle
                  key={`reg-${idx}`} cx={p.x} cy={p.y} r="4" fill="#ffffff" stroke="#94a3b8" strokeWidth="2"
                  onMouseEnter={() => setActiveTooltip({ type: 'Registered', val: trendData[idx].registered, date: trendData[idx].label })}
                  onMouseLeave={() => setActiveTooltip(null)}
                />
              ))}
              {attPoints.map((p, idx) => (
                <circle
                  key={`att-${idx}`} cx={p.x} cy={p.y} r="5" fill="#4f46e5" stroke="#ffffff" strokeWidth="2"
                  onMouseEnter={() => setActiveTooltip({ type: 'Attended', val: trendData[idx].attended, date: trendData[idx].label })}
                  onMouseLeave={() => setActiveTooltip(null)}
                />
              ))}
            </svg>
          ) : (
            <div className="empty-chart-notice">No conducted session trend data for selected range.</div>
          )}

          <div className="trend-x-labels">
            {trendData.map((d, i) => (
              <span key={i} className="trend-label" style={{ left: `${(i / Math.max(trendData.length - 1, 1)) * 90 + 5}%` }}>
                {d.label}
              </span>
            ))}
          </div>

          {activeTooltip && (
            <div className="trend-active-tooltip">
              <strong>{activeTooltip.date}</strong>: {activeTooltip.val} {activeTooltip.type}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── SUB-COMPONENT 4: Detailed Class Schedule Table (Report 1) ──
const ClassTable = ({ classes, onSelectClass, todayStr }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredClasses = useMemo(() => {
    return classes.filter(cls => {
      const matchSearch = searchTerm.trim() === '' ||
        (cls.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (cls.trainer || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (cls.id || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchLevel = selectedLevel === 'All' || cls.level === selectedLevel;
      
      const matchStatus = selectedStatus === 'All' ||
        (selectedStatus === 'Upcoming' && cls.status === 'Conducted' && cls.startDate > todayStr) ||
        (selectedStatus === 'Conducted' && cls.status === 'Conducted' && (!cls.startDate || cls.startDate <= todayStr)) ||
        (selectedStatus === 'Postponed' && cls.status === 'Postponed') ||
        (selectedStatus === 'Cancelled' && cls.status === 'Cancelled');

      return matchSearch && matchLevel && matchStatus;
    });
  }, [classes, searchTerm, selectedLevel, selectedStatus, todayStr]);

  const totalPages = Math.ceil(filteredClasses.length / rowsPerPage) || 1;
  const validPage = Math.min(currentPage, totalPages);

  const paginatedClasses = useMemo(() => {
    const startIdx = (validPage - 1) * rowsPerPage;
    return filteredClasses.slice(startIdx, startIdx + rowsPerPage);
  }, [filteredClasses, validPage, rowsPerPage]);

  const getLevelBadgeClass = (level) => {
    switch (level) {
      case 'Awareness': return 'badge-level-awareness';
      case 'Entry': return 'badge-level-entry';
      case 'Intermediate': return 'badge-level-intermediate';
      case 'Advanced': return 'badge-level-advanced';
      default: return 'badge-level-others';
    }
  };

  const getStatusBadge = (cls) => {
    if (cls.status === 'Cancelled') {
      return <span className="badge-pill badge-status-cancelled">Cancelled</span>;
    }
    if (cls.status === 'Postponed') {
      return <span className="badge-pill badge-status-postponed-legacy">Postponed (legacy)</span>;
    }
    if (cls.status === 'Conducted' && cls.startDate > todayStr) {
      return <span className="badge-pill badge-status-upcoming">Upcoming</span>;
    }
    return <span className="badge-pill badge-status-conducted">Conducted</span>;
  };

  return (
    <div className="report-table-section">
      <div className="table-header-controls">
        <div className="table-header-left">
          <div className="table-title-box">
            <SvgIcon name="book" size={20} />
            <h3 className="table-title">Detailed Class Schedule & Breakdown Log</h3>
          </div>
          <span className="table-count-badge">
            Showing {filteredClasses.length} of {classes.length} Classes
          </span>
        </div>

        <div className="table-header-right">
          <div className="table-search-box">
            <SvgIcon name="search" size={16} />
            <input
              type="text"
              placeholder="Search class title or trainer..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="table-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div className="table-filter-select-wrapper">
            <label htmlFor="filter-level">Level:</label>
            <select
              id="filter-level"
              value={selectedLevel}
              onChange={(e) => {
                setSelectedLevel(e.target.value);
                setCurrentPage(1);
              }}
              className="table-select"
            >
              {LEVEL_OPTIONS.map(lvl => (
                <option key={lvl} value={lvl}>{lvl}</option>
              ))}
            </select>
          </div>

          <div className="table-filter-select-wrapper">
            <label htmlFor="filter-status">Status:</label>
            <select
              id="filter-status"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="table-select"
            >
              {STATUS_OPTIONS.map(st => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="report-table-wrapper">
        <table className="report-data-table">
          <thead>
            <tr>
              <th style={{ width: '35px', textAlign: 'center' }}>No</th>
              <th style={{ minWidth: '200px' }}>Name of Class</th>
              <th style={{ minWidth: '140px' }}>Date & Time</th>
              <th>Level</th>
              <th style={{ textAlign: 'center', width: '105px' }}>Delivery Mode</th>
              <th>Status</th>
              <th style={{ textAlign: 'center' }}>Registered</th>
              <th style={{ textAlign: 'center' }}>Attended</th>
              <th style={{ textAlign: 'center' }}>Turn-up</th>
              <th style={{ minWidth: '150px' }}>Sector Breakdown (Gov / Priv / Stu)</th>
              <th style={{ textAlign: 'center', width: '95px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedClasses.length > 0 ? (
              paginatedClasses.map((cls, idx) => {
                const rowNo = (validPage - 1) * rowsPerPage + idx + 1;
                const hasRegs = cls.registeredPax > 0;
                const turnout = hasRegs
                  ? ((cls.attendedPax / cls.registeredPax) * 100).toFixed(1)
                  : 'N/A';
                const modeStr = getCourseDeliveryMode(cls);

                return (
                  <tr key={cls.id || idx} className="report-table-row">
                    <td style={{ textAlign: 'center', fontWeight: '600', color: '#64748b' }}>
                      {rowNo}
                    </td>
                    <td>
                      <div className="class-title-cell">
                        <span className="class-title-text">{cls.title}</span>
                        <div className="class-meta-sub">
                          <span className="class-id-tag">{cls.id}</span>
                          <span className="class-trainer-text">Trainer: {cls.trainer}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="class-datetime-cell">
                        <span className="date-range-text">{cls.startDate} to {cls.endDate}</span>
                        <span className="time-duration-text">{cls.startTime} - {cls.endTime} ({cls.durationHours}h)</span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge-pill ${getLevelBadgeClass(cls.level)}`}>
                        {cls.level}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`badge-pill badge-mode-${modeStr.toLowerCase()}`}>
                        {modeStr}
                      </span>
                    </td>
                    <td>
                      {getStatusBadge(cls)}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>
                      {cls.registeredPax}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: '700', color: '#0f172a' }}>
                      {cls.attendedPax}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {hasRegs ? (
                        <span className="turnout-badge turnout-neutral">
                          {turnout}%
                        </span>
                      ) : (
                        <span className="turnout-badge turnout-na">
                          N/A
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="sector-mini-pills">
                        <span className="mini-pill gov">
                          Gov: {cls.sectorBreakdown?.attended?.government || 0}
                        </span>
                        <span className="mini-pill priv">
                          Priv: {cls.sectorBreakdown?.attended?.private || 0}
                        </span>
                        <span className="mini-pill stu">
                          Stu: {cls.sectorBreakdown?.attended?.student || 0}
                        </span>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="btn-view-class-details"
                        onClick={() => onSelectClass(cls)}
                        title="View Full Class Details, Demographics & Remarks"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="12" className="empty-table-message">
                  No classes match your search query or selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="table-pagination-footer">
        <div className="pagination-info">
          <span>Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setCurrentPage(1);
            }}
            className="pagination-select"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span className="pagination-range-text">
            {filteredClasses.length > 0
              ? `${(validPage - 1) * rowsPerPage + 1}-${Math.min(validPage * rowsPerPage, filteredClasses.length)} of ${filteredClasses.length}`
              : '0 of 0'}
          </span>
        </div>

        <div className="pagination-controls">
          <button
            type="button"
            className="page-nav-btn"
            disabled={validPage <= 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            title="Previous Page"
          >
            <SvgIcon name="chevronLeft" size={16} />
            Prev
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
            <button
              key={pageNum}
              type="button"
              className={`page-num-btn ${pageNum === validPage ? 'active' : ''}`}
              onClick={() => setCurrentPage(pageNum)}
            >
              {pageNum}
            </button>
          ))}

          <button
            type="button"
            className="page-nav-btn"
            disabled={validPage >= totalPages}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            title="Next Page"
          >
            Next
            <SvgIcon name="chevronRight" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── SUB-COMPONENT 5: Per-Class Detail Modal (Report 1 Items) ──
const ClassDetailModal = ({ classItem, onClose, todayStr }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!classItem) return null;

  const hasRegs = classItem.registeredPax > 0;
  const turnout = hasRegs
    ? ((classItem.attendedPax / classItem.registeredPax) * 100).toFixed(1)
    : 'N/A';

  const totalAttendedGender = (classItem.genderBreakdown?.male || 0) +
    (classItem.genderBreakdown?.female || 0) +
    (classItem.genderBreakdown?.preferNotToSay || 0);

  const malePct = totalAttendedGender > 0 ? ((classItem.genderBreakdown?.male / totalAttendedGender) * 100).toFixed(1) : '0';
  const femalePct = totalAttendedGender > 0 ? ((classItem.genderBreakdown?.female / totalAttendedGender) * 100).toFixed(1) : '0';
  const otherPct = totalAttendedGender > 0 ? ((classItem.genderBreakdown?.preferNotToSay / totalAttendedGender) * 100).toFixed(1) : '0';

  const regMale = classItem.registeredGenderBreakdown?.male ?? classItem.genderBreakdown?.registered?.male ?? 0;
  const regFemale = classItem.registeredGenderBreakdown?.female ?? classItem.genderBreakdown?.registered?.female ?? 0;
  const regOther = classItem.registeredGenderBreakdown?.preferNotToSay ?? classItem.genderBreakdown?.registered?.preferNotToSay ?? 0;
  const totalRegGender = regMale + regFemale + regOther;
  const regMalePct = totalRegGender > 0 ? ((regMale / totalRegGender) * 100).toFixed(1) : '0';
  const regFemalePct = totalRegGender > 0 ? ((regFemale / totalRegGender) * 100).toFixed(1) : '0';
  const regOtherPct = totalRegGender > 0 ? ((regOther / totalRegGender) * 100).toFixed(1) : '0';

  const deliveryMode = getCourseDeliveryMode(classItem);
  const trainingType = getCourseTrainingType(classItem);

  let statusBadgeText = classItem.status;
  let statusBadgeClass = `status-${(classItem.status || 'conducted').toLowerCase()}`;
  if (classItem.status === 'Conducted' && classItem.startDate > todayStr) {
    statusBadgeText = 'Upcoming';
    statusBadgeClass = 'status-upcoming';
  } else if (classItem.status === 'Postponed') {
    statusBadgeText = 'Postponed (legacy)';
    statusBadgeClass = 'status-postponed-legacy';
  }

  return (
    <div className="report-modal-backdrop" onClick={onClose}>
      <div className="report-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="report-modal-header">
          <div className="modal-header-text">
            <div className="modal-badge-row">
              <span className="modal-id-badge">{classItem.id}</span>
              <span className="modal-cert-badge">{classItem.certificationBody}</span>
              <span className={`modal-status-badge ${statusBadgeClass}`}>
                {statusBadgeText}
              </span>
            </div>
            <h2 className="modal-class-title">{classItem.title}</h2>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <SvgIcon name="x" size={20} />
          </button>
        </div>

        <div className="report-modal-body">
          <div className="modal-meta-grid">
            <div className="meta-item">
              <span className="meta-label">Date & Time</span>
              <span className="meta-value">{classItem.startDate} to {classItem.endDate}</span>
              <span className="meta-sub">{classItem.startTime} - {classItem.endTime}</span>
            </div>

            <div className="meta-item">
              <span className="meta-label">Duration & Level</span>
              <span className="meta-value">{classItem.durationHours} Hours</span>
              <span className="meta-sub">Level: {classItem.level}</span>
            </div>

            <div className="meta-item">
              <span className="meta-label">Trainer & Venue</span>
              <span className="meta-value">{classItem.trainer}</span>
              <span className="meta-sub">{classItem.venue}</span>
            </div>

            <div className="meta-item">
              <span className="meta-label">Delivery Mode</span>
              <span className="meta-value">{deliveryMode}</span>
              <span className="meta-sub">Training Type: {trainingType}</span>
            </div>
          </div>

          <div className="modal-metrics-row">
            <div className="modal-metric-card">
              <span className="metric-label">Registered Pax</span>
              <span className="metric-val">{classItem.registeredPax}</span>
            </div>
            <div className="modal-metric-card">
              <span className="metric-label">Attended Pax</span>
              <span className="metric-val" style={{ color: '#2563eb' }}>{classItem.attendedPax}</span>
            </div>
            <div className="modal-metric-card">
              <span className="metric-label">Turn-up Rate</span>
              <span className="metric-val" style={{ color: '#0f172a' }}>
                {hasRegs ? `${turnout}%` : 'N/A'}
              </span>
            </div>
          </div>

          <div className="modal-detail-section">
            <h4 className="detail-section-title">
              <SvgIcon name="building" size={16} />
              Sector Breakdown (Registered vs. Attended)
            </h4>
            <div className="modal-table-wrap">
              <table className="modal-sub-table">
                <thead>
                  <tr>
                    <th>Sector</th>
                    <th style={{ textAlign: 'center' }}>Registered (Pax)</th>
                    <th style={{ textAlign: 'center' }}>Attended (Pax)</th>
                    <th style={{ textAlign: 'center' }}>Turn-up (%)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Government</strong></td>
                    <td style={{ textAlign: 'center' }}>{classItem.sectorBreakdown?.registered?.government || 0}</td>
                    <td style={{ textAlign: 'center', fontWeight: '700' }}>{classItem.sectorBreakdown?.attended?.government || 0}</td>
                    <td style={{ textAlign: 'center' }}>
                      {(classItem.sectorBreakdown?.registered?.government || 0) > 0
                        ? `${(((classItem.sectorBreakdown?.attended?.government || 0) / classItem.sectorBreakdown.registered.government) * 100).toFixed(1)}%`
                        : 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Private Sector</strong></td>
                    <td style={{ textAlign: 'center' }}>{classItem.sectorBreakdown?.registered?.private || 0}</td>
                    <td style={{ textAlign: 'center', fontWeight: '700' }}>{classItem.sectorBreakdown?.attended?.private || 0}</td>
                    <td style={{ textAlign: 'center' }}>
                      {(classItem.sectorBreakdown?.registered?.private || 0) > 0
                        ? `${(((classItem.sectorBreakdown?.attended?.private || 0) / classItem.sectorBreakdown.registered.private) * 100).toFixed(1)}%`
                        : 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Student</strong></td>
                    <td style={{ textAlign: 'center' }}>{classItem.sectorBreakdown?.registered?.student || 0}</td>
                    <td style={{ textAlign: 'center', fontWeight: '700' }}>{classItem.sectorBreakdown?.attended?.student || 0}</td>
                    <td style={{ textAlign: 'center' }}>
                      {(classItem.sectorBreakdown?.registered?.student || 0) > 0
                        ? `${(((classItem.sectorBreakdown?.attended?.student || 0) / classItem.sectorBreakdown.registered.student) * 100).toFixed(1)}%`
                        : 'N/A'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="modal-detail-section">
            <h4 className="detail-section-title">
              <SvgIcon name="users" size={16} />
              Gender Demographics (Registered Participants)
            </h4>
            <div className="gender-pill-grid">
              <div className="gender-pill-card male">
                <span className="gender-pill-label">Registered Male</span>
                <span className="gender-pill-count">{regMale} pax ({regMalePct}%)</span>
              </div>
              <div className="gender-pill-card female">
                <span className="gender-pill-label">Registered Female</span>
                <span className="gender-pill-count">{regFemale} pax ({regFemalePct}%)</span>
              </div>
              <div className="gender-pill-card other">
                <span className="gender-pill-label">Registered Prefer Not To Say</span>
                <span className="gender-pill-count">{regOther} pax ({regOtherPct}%)</span>
              </div>
            </div>
          </div>

          <div className="modal-detail-section">
            <h4 className="detail-section-title">
              <SvgIcon name="users" size={16} />
              Gender Demographics (Attended Participants)
            </h4>
            <div className="gender-pill-grid">
              <div className="gender-pill-card male">
                <span className="gender-pill-label">Attended Male</span>
                <span className="gender-pill-count">{classItem.genderBreakdown?.male || 0} pax ({malePct}%)</span>
              </div>
              <div className="gender-pill-card female">
                <span className="gender-pill-label">Attended Female</span>
                <span className="gender-pill-count">{classItem.genderBreakdown?.female || 0} pax ({femalePct}%)</span>
              </div>
              <div className="gender-pill-card other">
                <span className="gender-pill-label">Attended Prefer Not To Say</span>
                <span className="gender-pill-count">{classItem.genderBreakdown?.preferNotToSay || 0} pax ({otherPct}%)</span>
              </div>
            </div>
          </div>

          <div className="modal-detail-section">
            <h4 className="detail-section-title">
              <SvgIcon name="info" size={16} />
              Special Remarks & Session Notes
            </h4>
            <div className="modal-remarks-box">
              <p>{classItem.remarks || 'No special remarks recorded for this session.'}</p>
            </div>
          </div>
        </div>

        <div className="report-modal-footer">
          <button
            type="button"
            className="btn-modal-close"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};



// ── SUB-COMPONENT 7: Unified Export Modal (CSV Only) ──
const ExportModal = ({
  isOpen,
  onClose,
  initialStartDate,
  initialEndDate,
  onPerformExport
}) => {
  const [reportType, setReportType] = useState('summary'); // 'summary' | 'breakdown' | 'learnerLog'
  const [modalStartDate, setModalStartDate] = useState(initialStartDate);
  const [modalEndDate, setModalEndDate] = useState(initialEndDate);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setModalStartDate(initialStartDate);
      setModalEndDate(initialEndDate);
      setExportError(null);
    }
  }, [isOpen, initialStartDate, initialEndDate]);

  if (!isOpen) return null;

  const isDateValid = modalStartDate && modalEndDate && modalStartDate <= modalEndDate;

  const handleExport = async () => {
    if (!isDateValid) return;
    setExportLoading(true);
    setExportError(null);
    try {
      await onPerformExport({
        reportType,
        startDate: modalStartDate,
        endDate: modalEndDate
      });
      onClose();
    } catch (err) {
      console.error('Export error:', err);
      setExportError(err.message || 'Failed to export report.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="report-modal-backdrop" onClick={onClose}>
      <div className="report-modal-content export-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="report-modal-header">
          <div className="modal-header-text">
            <h2 className="modal-class-title">Export Analytics Report</h2>
            <span className="modal-sub-caption">Choose report type and specify date range to export as CSV</span>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <SvgIcon name="x" size={20} />
          </button>
        </div>

        <div className="report-modal-body">
          {/* 1. Report Choice */}
          <div className="export-field-group">
            <label className="export-group-label">1. Select Report Type <span className="req-star">*</span></label>
            <div className="export-report-options">
              <label className={`export-radio-card ${reportType === 'summary' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="reportSelection"
                  value="summary"
                  checked={reportType === 'summary'}
                  onChange={() => setReportType('summary')}
                />
                <div className="radio-card-content">
                  <div className="radio-card-header">
                    <SvgIcon name="barChart" size={18} />
                    <strong>Report 1: Summary Report</strong>
                  </div>
                  <p className="radio-card-desc">
                    Executive KPIs, Sector (Reg/Att/Turnout), Class by Level, Participants by Level, Type of Courses, Mode of Delivery, and Gender.
                  </p>
                </div>
              </label>

              <label className={`export-radio-card ${reportType === 'breakdown' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="reportSelection"
                  value="breakdown"
                  checked={reportType === 'breakdown'}
                  onChange={() => setReportType('breakdown')}
                />
                <div className="radio-card-content">
                  <div className="radio-card-header">
                    <SvgIcon name="book" size={18} />
                    <strong>Report 2: Class Breakdown Report</strong>
                  </div>
                  <p className="radio-card-desc">
                    Full per-class schedule breakdown log with delivery mode, sector, gender, turnout, and remarks.
                  </p>
                </div>
              </label>

              <label className={`export-radio-card ${reportType === 'learnerLog' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="reportSelection"
                  value="learnerLog"
                  checked={reportType === 'learnerLog'}
                  onChange={() => setReportType('learnerLog')}
                />
                <div className="radio-card-content">
                  <div className="radio-card-header">
                    <SvgIcon name="users" size={18} />
                    <strong>Report 3: Learner Registration Log</strong>
                  </div>
                  <p className="radio-card-desc">
                    Individual participant records with username, gender, sector, course name, dates, registration status, and participation status.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* 2. Date Range */}
          <div className="export-field-group">
            <label className="export-group-label">2. Date Range (Required) <span className="req-star">*</span></label>
            <div className="export-date-row">
              <div className="export-date-field-wrap">
                <label htmlFor="export-from-date">From:</label>
                <input
                  id="export-from-date"
                  type="date"
                  className="report-date-field"
                  value={modalStartDate}
                  onChange={(e) => setModalStartDate(e.target.value)}
                />
              </div>
              <span className="date-separator">→</span>
              <div className="export-date-field-wrap">
                <label htmlFor="export-to-date">To:</label>
                <input
                  id="export-to-date"
                  type="date"
                  className="report-date-field"
                  value={modalEndDate}
                  onChange={(e) => setModalEndDate(e.target.value)}
                />
              </div>
            </div>
            {!isDateValid && (
              <span className="export-validation-msg">
                Please provide valid From and To dates where From is before or equal to To.
              </span>
            )}
          </div>

          {exportError && (
            <div className="export-error-notice">
              {exportError}
            </div>
          )}
        </div>

        <div className="report-modal-footer export-footer-actions">
          <button
            type="button"
            className="btn-modal-cancel"
            onClick={onClose}
            disabled={exportLoading}
          >
            Cancel
          </button>

          <div className="export-action-buttons">
            <button
              type="button"
              className="export-btn export-excel"
              disabled={!isDateValid || exportLoading}
              onClick={handleExport}
            >
              <SvgIcon name="download" size={16} />
              <span>{exportLoading ? 'Exporting...' : 'Export Excel (.csv)'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =========================================================================
// MAIN COMPONENT: ReportDashboard
// =========================================================================
const ReportDashboard = ({ user, initialOpenExportModal = false }) => {
  const [dbClasses, setDbClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(`${currentYear}-12-31`);

  const [selectedClass, setSelectedClass] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(Boolean(initialOpenExportModal));

  useEffect(() => {
    if (initialOpenExportModal) {
      setIsExportModalOpen(true);
    }
  }, [initialOpenExportModal]);

  const todayStr = useMemo(() => getTodayDateStr(), []);

  const fetchLiveReportData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch('/api/admin/reports/classes-summary');
      const json = await res.json();
      if (json.status === 'success' && Array.isArray(json.data)) {
        setDbClasses(json.data);
      } else {
        setFetchError(json.message || 'Failed to fetch class records from database.');
      }
    } catch (err) {
      console.error('Failed to fetch reporting data:', err);
      setFetchError('Could not connect to backend server.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveReportData();
  }, [fetchLiveReportData]);

  // Filter classes according to date range (Rule 6.3: include if StartDate falls inside range)
  const filteredClasses = useMemo(() => {
    return dbClasses.filter(cls => {
      if (!startDate && !endDate) return true;
      const classStart = cls.startDate;
      if (startDate && classStart < startDate) return false;
      if (endDate && classStart > endDate) return false;
      return true;
    });
  }, [dbClasses, startDate, endDate]);

  // Compute summary using single unified function
  const summaryMetrics = useMemo(() => {
    return computeSummary(filteredClasses, todayStr);
  }, [filteredClasses, todayStr]);

  // Attendance Trend: Weekly (if range <= 92 days) or Monthly (longer ranges)
  const trendSummary = useMemo(() => {
    const validClasses = summaryMetrics.validConductedClasses || [];
    if (validClasses.length === 0) return [];

    let isWeekly = true;
    if (startDate && endDate) {
      const diffDays = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
      if (diffDays > 92) isWeekly = false;
    } else {
      isWeekly = false;
    }

    const bucketMap = new Map();

    validClasses.forEach(c => {
      if (!c.startDate) return;
      let bucketKey = '';
      if (isWeekly) {
        // Week grouping
        const d = new Date(c.startDate);
        const dayOfWeek = d.getDay();
        const monday = new Date(d);
        monday.setDate(d.getDate() - ((dayOfWeek + 6) % 7));
        bucketKey = `${monday.toLocaleString('en-US', { month: 'short' })} ${monday.getDate()}`;
      } else {
        // Month grouping (e.g. 'Jan 2026')
        const d = new Date(c.startDate);
        bucketKey = d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
      }

      if (!bucketMap.has(bucketKey)) {
        bucketMap.set(bucketKey, { label: bucketKey, registered: 0, attended: 0, sortKey: c.startDate });
      }
      const b = bucketMap.get(bucketKey);
      b.registered += (c.registeredPax || 0);
      b.attended += (c.attendedPax || 0);
    });

    const result = Array.from(bucketMap.values());
    result.sort((a, b) => (a.sortKey > b.sortKey ? 1 : -1));
    return result;
  }, [summaryMetrics.validConductedClasses, startDate, endDate]);

  // Unified export performer
  const handlePerformExport = async ({ reportType, startDate: exportStart, endDate: exportEnd }) => {
    let exportData = dbClasses;

    // Fetch targeted date range if different from current DB state
    try {
      const res = await fetch(`/api/admin/reports/classes-summary?startDate=${exportStart}&endDate=${exportEnd}`);
      const json = await res.json();
      if (json.status === 'success' && Array.isArray(json.data)) {
        exportData = json.data;
      }
    } catch (e) {
      console.warn('Could not fetch custom range, filtering existing state:', e);
      exportData = dbClasses.filter(cls => {
        if (!exportStart && !exportEnd) return true;
        const s = cls.startDate;
        if (exportStart && s < exportStart) return false;
        if (exportEnd && s > exportEnd) return false;
        return true;
      });
    }

    if (reportType === 'breakdown') {
      exportClassBreakdownCSV(exportData, exportStart, exportEnd, todayStr);
    } else if (reportType === 'learnerLog') {
      exportLearnerRegistrationLogCSV(exportData, exportStart, exportEnd, todayStr);
    } else {
      exportSummaryReportCSV(exportData, exportStart, exportEnd, todayStr);
    }
  };

  const getSectionTitle = (baseName) => baseName;

  return (
    <div className="training-report-dashboard">
      <header className="profile-top-bar no-print">
        <div className="profile-top-bar-left">
          <SvgIcon name="barChart" size={24} />
          <div>
            <h1 className="admin-dashboard-title">Training & Class Analytics Dashboard</h1>
            <p className="admin-dashboard-subtitle">
              Live operational reporting and attendance intelligence from MySQL database.
            </p>
          </div>
        </div>
        <div className="profile-top-bar-right" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            type="button"
            className="filter-reset-btn"
            onClick={fetchLiveReportData}
            title="Refresh records"
          >
            <SvgIcon name="refresh" size={14} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </header>

      <div className="report-dashboard-content">
        <section className="report-section-block">
          <ReportFilters
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            onOpenExportModal={() => setIsExportModalOpen(true)}
          />
        </section>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#64748b' }}>
            <div className="sync-pulse-dot" style={{ margin: '0 auto 1rem auto', width: '16px', height: '16px' }} />
            <p style={{ fontWeight: 600 }}>Loading live class analytics from database...</p>
          </div>
        ) : fetchError ? (
          <div style={{ textAlign: 'center', padding: '3rem 2rem', background: '#fee2e2', color: '#991b1b', borderRadius: '12px' }}>
            <p style={{ fontWeight: 700 }}>{fetchError}</p>
            <button
              type="button"
              className="export-btn export-unified"
              onClick={fetchLiveReportData}
              style={{ marginTop: '1rem', background: '#dc2626' }}
            >
              Retry Connection
            </button>
          </div>
        ) : (
          <>
            {/* ════════════════════════════════════════════════════════════
                REPORT 1: SUMMARY REPORT SECTION
            ════════════════════════════════════════════════════════════ */}
            <section className="report-major-section" id="section-summary-report">
              <div className="major-section-header">
                <div className="major-section-badge">Report 1</div>
                <h2 className="major-section-title">{getSectionTitle('Summary Report')}</h2>
                <span className="major-section-subtitle">
                  Executive KPI cards, sector distributions, level summaries, and timeline intelligence
                </span>
              </div>

              <div className="report-section-block">
                <div className="section-block-header">
                  <h3 className="section-block-title">Executive Summary Overview</h3>
                  <span className="section-block-caption">Key performance indicators for conducted sessions</span>
                </div>
                <KPISummary kpis={summaryMetrics} />
              </div>

              <div className="report-section-block" style={{ marginTop: '1.5rem' }}>
                <div className="section-block-header">
                  <h3 className="section-block-title">Visual Analytics & Distribution Grid</h3>
                  <span className="section-block-caption">Multi-dimensional breakdown across sector, level, gender and timeline</span>
                </div>
                <ReportCharts
                  sectorData={summaryMetrics.sector}
                  levelData={summaryMetrics.level}
                  genderData={summaryMetrics.gender}
                  trendData={trendSummary}
                  totalConducted={summaryMetrics.totalClassesConducted}
                  totalAttendance={summaryMetrics.totalAttendance}
                  trainingTypeData={summaryMetrics.trainingType}
                  deliveryModeData={summaryMetrics.deliveryMode}
                />
              </div>


            </section>

            {/* ════════════════════════════════════════════════════════════
                REPORT 2: CLASS BREAKDOWN REPORT SECTION
            ════════════════════════════════════════════════════════════ */}
            <section className="report-major-section" id="section-class-breakdown-report">
              <div className="major-section-header">
                <div className="major-section-badge badge-breakdown">Report 2</div>
                <h2 className="major-section-title">{getSectionTitle('Class Breakdown Report')}</h2>
                <span className="major-section-subtitle">
                  Detailed class schedule and session-level breakdown log with demographics and remarks
                </span>
              </div>

              <ClassTable
                classes={filteredClasses}
                onSelectClass={(cls) => setSelectedClass(cls)}
                todayStr={todayStr}
              />
            </section>
          </>
        )}
      </div>

      {selectedClass && (
        <ClassDetailModal
          classItem={selectedClass}
          onClose={() => setSelectedClass(null)}
          todayStr={todayStr}
        />
      )}

      {isExportModalOpen && (
        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          initialStartDate={startDate}
          initialEndDate={endDate}
          onPerformExport={handlePerformExport}
        />
      )}
    </div>
  );
};

export default ReportDashboard;
